import { Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, delay, scan, shareReplay, tap } from 'rxjs/operators';
import { NotificationService } from 'src/app/services/notification/notification.service';

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AlertController, ModalController, RefresherCustomEvent } from '@ionic/angular';
import { ApiResponse, InfiniteScrollCustomEvent } from '@shared/models';
import { Billing, EditModel } from '@shared/models/billing.model';

import { BillingService } from '../billing-info/services/billing.service';
import { ProfileBillsAddPage } from '../profile-bills-add/profile-bills-add.page';

@Component({
  selector: 'app-billing-selection',
  templateUrl: './billing-selection.component.html',
  styleUrls: ['./billing-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingSelectionComponent {
  loadingBillings = true;
  private loadBillingssSubject = new Subject<number>();
  infiniteScroll: InfiniteScrollCustomEvent | null = null;
  billings$ = this.loadBillingssSubject.asObservable().pipe(
    delay(0),
    tap(() => (this.loadingBillings = true)),
    concatMap(() => this.getBillings(1)),
    scan((acc, response) => this.accumulateResults(acc, response)),
    tap(() => {
      this.loadingBillings = false;
      this.infiniteScroll?.target.complete();
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  selectedBilling: Billing | null = null;

  constructor(
    private billingService: BillingService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private notificationService: NotificationService
  ) {}

  ionViewDidEnter() {
    this.refresh();
  }

  doRefresh(refresh: RefresherCustomEvent) {
    this.loadBillingssSubject.next(1);
    setTimeout(() => {
      refresh.target.complete();
    }, 1000);
  }

  doInfinite<T>(infiniteScroll: InfiniteScrollCustomEvent, list: ApiResponse<T[]>) {
    if (list.current_page === list.last_page) {
      infiniteScroll.target.complete();
      return;
    }
    this.loadBillingssSubject.next(list.current_page ? list.current_page + 1 : 1);
    setTimeout(() => {
      infiniteScroll.target.complete();
    }, 1000);
  }

  addBillingInfo(billing: Billing | null = null, event: Event | null = null) {
    event?.stopPropagation();
    this.modalCtrl
      .create({
        component: ProfileBillsAddPage,
        componentProps: {
          billing,
        },
      })
      .then((modal) => {
        modal.present();
        modal.onDidDismiss().then(() => {
          this.refresh();
        });
      });
  }

  deleteBillingInfo(billing: EditModel<Billing>, event: Event) {
    event.stopPropagation();
    this.alertCtrl
      .create({
        header: '¿Estás seguro?',
        message: '¿Quieres eliminar este registro?',
        buttons: [
          {
            text: 'Cancelar',
            role: 'cancel',
          },
          {
            text: 'Eliminar',
            handler: () => {
              this.billingService.deleteBilling(billing.id).subscribe(() => {
                this.notificationService.presentToast('Se ha eliminado el registro correctamente', 3000, 'top');
                this.refresh();
              });
            },
          },
        ],
      })
      .then((alert) => {
        alert.present();
      });
  }

  selectBilling(billing: Billing) {
    this.selectedBilling = billing;
  }

  back() {
    this.modalCtrl.dismiss();
  }

  confirmSelection() {
    this.modalCtrl.dismiss({ billing: this.selectedBilling });
  }

  private getBillings(page: number): Observable<ApiResponse<Billing[]>> {
    return this.billingService.getBillings({ page }).pipe(catchError(() => of({ data: [] })));
  }

  private refresh() {
    this.loadBillingssSubject.next(1);
  }

  private accumulateResults<T>(acc: ApiResponse<T[]>, response: ApiResponse<T[]>): ApiResponse<T[]> {
    if (!response) {
      return acc;
    }
    if (response.current_page === 1) {
      return response;
    }
    return {
      ...response,
      data: [...acc.data, ...response.data],
    };
  }
}
